import type { Professor } from "@/types"

/**
 * How the product addresses a professor (PRD-59).
 *
 * In Mexico a teacher is "la profe" or "el profe" to students, colleagues and
 * parents alike — it is the ordinary register, not a formality. The app
 * greeted them by bare first name ("Hola, Ana"), which is how a consumer app
 * greets a user, not how a school addresses a teacher.
 *
 * Two rules, both about not overstepping:
 *
 *  1. If the professor already wrote an honorific into their own name, that
 *     is them telling us how they want to be addressed — we keep theirs
 *     verbatim ("Profa. Ana", "Mtro. Luis"), never replace it and never
 *     stack a second one on top.
 *  2. Otherwise we use "Profe", which carries no gender. `full_name` is a
 *     free-text field; inferring "Profa." from a name would be guessing at
 *     someone's gender from their first name, which this product does not do.
 */

// Written forms a professor plausibly types, in either locale. Compared
// case- and accent-insensitively against the first token of the name.
const HONORIFICS = [
  "prof",
  "profe",
  "profa",
  "profr",
  "profesor",
  "profesora",
  "mtro",
  "mtra",
  "maestro",
  "maestra",
  "lic",
  "licda",
  "dr",
  "dra",
  "ing",
  "arq",
  "teacher",
  "mr",
  "mrs",
  "ms",
] as const

const fold = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\./g, "").toLowerCase()

/** The source string to read a name from: what they typed, richest first. */
const nameSource = (professor: Pick<Professor, "full_name" | "name" | "email">): string =>
  professor.full_name?.trim() || professor.name?.trim() || professor.email?.split("@")[0] || ""

const tokens = (professor: Pick<Professor, "full_name" | "name" | "email">): string[] =>
  nameSource(professor).split(/\s+/).filter(Boolean)

/** The honorific the professor wrote, verbatim, or `null`. */
export function writtenHonorific(
  professor: Pick<Professor, "full_name" | "name" | "email">,
): string | null {
  const [first] = tokens(professor)
  if (!first) return null
  // A lone honorific with nothing after it is a name, not a title.
  if (tokens(professor).length < 2) return null
  return HONORIFICS.includes(fold(first) as (typeof HONORIFICS)[number]) ? first : null
}

/** Given name, with any honorific the professor wrote stripped off. */
export function professorFirstName(
  professor: Pick<Professor, "full_name" | "name" | "email">,
): string {
  const parts = tokens(professor)
  if (parts.length === 0) return ""
  return writtenHonorific(professor) ? (parts[1] ?? parts[0]!) : parts[0]!
}

/**
 * What a greeting calls them: their own honorific when they wrote one, else
 * "Profe". `fallbackHonorific` lets the en-US locale pass "Teacher".
 */
export function professorGreetingName(
  professor: Pick<Professor, "full_name" | "name" | "email">,
  fallbackHonorific: string,
): string {
  const given = professorFirstName(professor)
  if (!given) return fallbackHonorific
  const written = writtenHonorific(professor)
  return `${written ?? fallbackHonorific} ${given}`.trim()
}
